import FutureThumbnail from '@assets/media/card_next_cover.png';
import { setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { Column } from 'common/components/common/style';
import { NoNetworkPopup } from 'common/components/no-network-popup';
import { Progressbar } from 'common/components/progressbar';
import SkeletonLoader from 'common/components/skeleton-loader/SkeletonLoader';
import { THUMBNAIL_OFFSET } from 'common/config/constants';
import variables from 'common/config/variables';
import { usePopup } from 'common/contexts/popup';
import { IVideoPlayer } from 'common/reducers/videoPlayer';
import { getThumbnailUrl, getToday } from 'common/services/helpers';
import { getClassName, getScalablePixel } from 'common/utils';
import AnimationDurations from 'common/utils/AnimationDurations';
import { useHoverable, useOnlineStatus, usePlayerActions } from 'common/utils/hooks';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { PlayerControlsProps } from './Controls';
import { EpgShowContainer, EpgShowThumbnail, EpgShowType } from './Controls.styles';
import EpgShowInfo from './EpgShowInfo';

const EpgShowSkeleton = (props: { type: EpgShowType }) => (
  <div
    className={getClassName('flex', {
      row: props.type === 'prev',
      'row-reverse': props.type === 'next'
    })}
  >
    <SkeletonLoader width={getScalablePixel(249)} height={getScalablePixel(120)} margin />
    <div className="flex column">
      <SkeletonLoader width={getScalablePixel(257)} height={getScalablePixel(18)} round />
      <SkeletonLoader width={getScalablePixel(257)} height={getScalablePixel(18)} round />
    </div>
  </div>
);

interface EpgShowProps {
  type: EpgShowType;
  togglePlay: PlayerControlsProps['togglePlay'];
  toggleControls: PlayerControlsProps['toggleControls'];
  // Redux injected
  epgs?: IVideoPlayer['epgs'];
  vod?: boolean;
  //
}

function EpgShow(props: EpgShowProps) {
  const [isOnline] = useOnlineStatus();
  const isOnlineRef = useRef(isOnline);
  const { openPopup } = usePopup();
  const { playVideoFromContinueWatching: playVideo } = usePlayerActions();
  const epg = useMemo(
    () => (!isEmpty(props.epgs) ? props.epgs[props.type] : undefined),
    [props.epgs, props.type]
  );

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  const onSelect = useCallback(() => {
    !isOnlineRef.current &&
      openPopup({
        id: 'no-network',
        returnToLastFocused: false,
        content: <NoNetworkPopup />
      });
    if (isEmpty(epg) || !isOnlineRef.current) {
      return;
    }
    const now = getToday().getTime() / 1000;
    // Future show
    if (epg.start > now) {
      return;
    }
    //
    props.togglePlay(false);
    AnimationDurations.set(
      'PLAYER_AUTOHIDE_TIMER',
      variables.PLAYER_MINIVIEW_EXTENDED_AUTOHIDE_TIMER
    );
    playVideo(epg, { continueWatching: true, isFullscreen: true });
  }, [epg]);

  const loading = useMemo(() => !epg, [epg]);
  // const loading = useMemo(() => true, [epg]);
  const visible = useMemo(() => !props.vod, [props.vod]);

  const { ref, focused } = useFocusable({
    onEnterPress: onSelect,
    focusable: visible && !loading
  });
  const { active, onMouseEnter, onMouseLeave } = useHoverable(focused);

  const isFuture = useMemo(
    () =>
      !isEmpty(epg) ? props.type === 'next' && epg.start > getToday().getTime() / 1000 : undefined,
    [epg, props.type]
  );
  const thumbnail = useMemo(() => {
    if (isEmpty(epg)) {
      return '';
    }
    const now = getToday().getTime() / 1000;
    // Future show
    if (isFuture) {
      return FutureThumbnail;
    }
    //
    const thumbnailTimestamp =
      now - epg.start < THUMBNAIL_OFFSET ? now : epg.start + THUMBNAIL_OFFSET;
    return getThumbnailUrl(epg.chan_id, epg.now, thumbnailTimestamp);
  }, [props.epgs?.prev?.start, props.epgs?.next?.start, props.type, isFuture]);

  useEffect(() => {
    // When no epg is found, set focus to play button
    if (focused && !epg) {
      setFocus('TOGGLE_PLAY');
    }
  }, [focused, epg]);

  const renderInfo = () => {
    if (!visible) return null;
    return loading ? (
      <EpgShowSkeleton type={props.type} />
    ) : (
      <>
        {props.type === 'next' && <EpgShowInfo key="next-epg-info" type={props.type} {...epg} />}
        <Column>
          <EpgShowThumbnail
            src={thumbnail}
            type={isFuture ? 'future' : props.type}
            focused={active}
            data-test-id={`epg-show-thumbnail-${props.type}`}
          />
          {epg?.skips?.continue && (
            <Progressbar safeArea={false} active={false} value={epg.skips.continue.progress} />
          )}
        </Column>
        {props.type === 'prev' && <EpgShowInfo key="prev-epg-info" type={props.type} {...epg} />}
      </>
    );
  };

  return (
    <EpgShowContainer
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onSelect}
      data-test-id={`epg-show-container-${props.type}`}
    >
      {renderInfo()}
    </EpgShowContainer>
  );
}

const mapStateToProps = ({ videoPlayer }: any) => {
  return {
    epgs: videoPlayer.epgs,
    vod: videoPlayer.video?.vod
  };
};
export default React.memo(connect(mapStateToProps, {})(EpgShow));
